import { Component, effect, inject, input, output, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import {
  AdminButtonComponent,
  AdminModalComponent,
  AdminTextareaComponent,
} from '../../admin';
import { AdminToastService } from '../../admin/admin-toast.service';
import { WhatsappService } from '../../services/whatsapp.service';

@Component({
  selector: 'app-whatsapp-modal',
  standalone: true,
  imports: [FormsModule, AdminModalComponent, AdminButtonComponent, AdminTextareaComponent],
  template: `
    @if (open()) {
      <app-admin-modal title="Send WhatsApp" (closed)="onCancel()">
        <div class="cg-admin-form-grid">
          <label class="cg-admin-field cg-admin-field--full">
            <span class="cg-admin-field__label">Phone number</span>
            <input
              class="cg-input"
              type="tel"
              name="phone"
              [(ngModel)]="phone"
              [disabled]="sending()"
              placeholder="9170XXXXXXXX"
            />
            <span class="cg-form-help">Country code + number, digits only (no + or spaces).</span>
          </label>

          <app-admin-textarea
            class="cg-admin-field--full"
            label="Message"
            name="message"
            [(value)]="message"
            [disabled]="sending()"
            [rows]="4"
            [fullWidth]="true"
          />
        </div>

        @if (formError()) {
          <p class="cg-form-error" role="alert">{{ formError() }}</p>
        }

        <div class="mt-4 flex flex-wrap justify-end gap-2">
          <app-admin-button variant="cancel" type="button" [disabled]="sending()" (click)="onCancel()">
            Cancel
          </app-admin-button>
          <app-admin-button variant="primary" type="button" [disabled]="sending()" (click)="onSend()">
            {{ sending() ? 'Sending…' : 'Send' }}
          </app-admin-button>
        </div>
      </app-admin-modal>
    }
  `,
})
export class WhatsappModalComponent {
  private readonly whatsappService = inject(WhatsappService);
  private readonly toast = inject(AdminToastService);

  readonly open = input(false);
  readonly leadId = input.required<string>();
  readonly defaultPhone = input('');

  readonly closed = output<void>();
  readonly sent = output<void>();

  readonly sending = signal(false);
  readonly formError = signal<string | null>(null);

  phone = '';
  message = 'Hello from CareerGrad CRM';

  constructor() {
    effect(() => {
      if (this.open()) {
        this.phone = this.defaultPhone();
        this.message = 'Hello from CareerGrad CRM';
        this.formError.set(null);
      }
    });
  }

  onCancel(): void {
    if (this.sending()) return;
    this.closed.emit();
  }

  onSend(): void {
    const phone = this.phone.replace(/\D/g, '');
    const text = this.message.trim();
    if (phone.length < 8) {
      this.formError.set('Enter a valid phone number with country code.');
      return;
    }
    if (!text) {
      this.formError.set('Message is required.');
      return;
    }

    this.sending.set(true);
    this.formError.set(null);

    this.whatsappService
      .sendMessage({ leadId: this.leadId(), phone, message: text })
      .subscribe({
        next: () => {
          this.sending.set(false);
          this.toast.success('WhatsApp message sent.');
          this.sent.emit();
          this.closed.emit();
        },
        error: (err: { error?: { message?: string } }) => {
          this.sending.set(false);
          const msg = err.error?.message ?? 'Could not send WhatsApp message.';
          this.formError.set(msg);
          this.toast.error(msg);
        },
      });
  }
}
